<template>
  <div class="card border-0 bg-white profile-settings-card rounded-0">
    <div class="card-body p-15 p-sm-20 p-md-25 p-lg-30 letter-spacing">
      <form v-on:submit="submit">
        <div class="row">
          <!-- NAME -->
          <div class="col-md-6">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                {{ $t('form.name.label') }}*
              </label>
              <input v-model="zone.name" class="form-control shadow-none text-black" required
                :class="[{ 'is-invalid': submitted && errors?.name }]" />
              <!-- ERRORS -->
              <div class="form-messages mt-2 px-1" v-if="errors?.name ?? false">
                <p v-for="error in errors.name" :key="error" class="small text-danger lh-1 m-0 mt-1">{{
                  error }}</p>
              </div>
            </div>
          </div>
          <div class="col-12"></div>

          <!-- LOCATION -->
          <!-- <div class="col-12">
            <h4 class="mb-15 mb-sm-20 mb-md-25">{{ $t('form.section.location') }}</h4>
          </div> -->
          <!-- COUNTRY -->
          <div class="col-md-6">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                {{ $t('form.country.label') }}*
              </label>
              <country-select v-model="zone.country_code" :country="zone.country_code"
                class="form-select shadow-none text-black fs-md-15" required
                :class="[{ 'is-invalid': submitted && errors?.country }]" whiteList="['IT','GB','FR','ES','DE']" />
              <!-- ERRORS -->
              <div class="form-messages mt-2 px-1" v-if="errors?.country ?? false">
                <p v-for="error in errors.country" :key="error" class="small text-danger lh-1 m-0 mt-1">{{
                  error }}</p>
              </div>
            </div>
          </div>
          <!-- STRATEGY -->
          <div class="col-12"></div>
          <div class="col-md-6">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                {{ $t('form.assignment_strategy.label') }}*
              </label>
              <div class="d-flex gap-3 align-items-center">
                <select class="form-select shadow-none text-black pe-50 fs-14 fw-medium"
                  v-model="zone.patient_assignment_strategy" required @change="changeStrategy()">
                  <option v-for="strategy in strategies" :value="strategy" :key="strategy">{{
                    $t(`form.assignment_strategy.values.${strategy}`) }}</option>
                </select>
              </div>
              <!-- ERRORS -->
              <div class="form-messages mt-2 px-1" v-if="errors?.lang ?? false">
                <p v-for="error in errors.lang" :key="error" class="small text-danger lh-1 m-0 mt-1">{{
                  error }}</p>
              </div>
            </div>
          </div>

          <div class="col-12">
            <div class="col-md-6">
              <div class="form-group mb-15 mb-sm-20 mb-md-25" v-if="locales.length > 1">
                <label class="d-block text-black fw-semibold mb-10">
                  {{ $t('feature.contents.lang') }}
                </label>
                <div class="d-flex gap-3 align-items-center">
                  <select class="form-select shadow-none text-black pe-50 fs-14 fw-medium w-auto"
                    v-model="zone.language_code" @change="changeLang()" required>
                    <option v-for="locale in locales" :value="locale" :key="locale">{{
                      $t(`feature.contents.$lang.${locale}`) }}</option>
                  </select>
                </div>
                <!-- ERRORS -->
                <div class="form-messages mt-2 px-1" v-if="errors?.lang ?? false">
                  <p v-for="error in errors.lang" :key="error" class="small text-danger lh-1 m-0 mt-1">{{
                    error }}</p>
                </div>
              </div>
            </div>
          </div>
          <div v-if="zone.patient_assignment_strategy == 'by_hcp'">
            <ZoneConsents v-model:hcp_consent_id="zone.consents_ids!.hcp_consent_id"  /><!-- @change="$event => zone.consents_ids.hcp_consents_id = $event" -->
          </div>
          <template v-if="update && !(zone.zone_roles?.length === 0)">
              <div  v-for="role in zone.zone_roles" :key="role.id">
              <RoleConsents :role="role"/>
            </div>
          </template>

          <ZoneJourneySchemas :zone_id="zone.id!" :roles="journey_roles" v-if="show_journey_schemas"/>

          <div class="col-12" v-if="loading">
            <SpinnerLoading />
          </div>
          <div class="col-md-12" v-else>
            <div class="d-flex align-items-center justify-content-between gap-3">
              <!-- DELETE -->
              <button v-if="update" type="button" class="bg-transparent p-0 border-0 text-danger lh-1 fw-medium"
                data-bs-toggle="modal" data-bs-target="#confirmModal">
                <i class="ph ph-trash lh-1 me-1 position-relative top-2"></i>
                <span class="position-relative">{{ $t('cta.delete_zone') }}</span>
              </button>
              <span v-else></span>
              <!-- SAVE -->
              <div class="d-flex flex-wrap gap-3 justify-content-end ms-auto">
                <DiscardButton @confirm="$router.push({ path: '/zones' })" />
                <button
                  class="default-btn transition fw-medium text-white pt-10 pb-10 ps-25 pe-25 pt-md-11 pb-md-11 ps-md-35 pe-md-35 fs-md-15 fs-lg-16"
                  type="submit" v-on:click="submitted = true">
                  {{ $t('cta.save_zone') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  </div>
  <!-- ERROR MESSAGE -->
  <div class="row justify-content-center">
    <div class="col">
      <div
        class="alert alert-danger alert-dismissible fade border-0 border-danger-subtle border-top rounded-0 m-0 mb-25"
        role="alert" id="errorMessage">
        <strong>{{ $t('error.something_went_wrong') }}</strong>
        <button type="button" class="btn-close" v-on:click="hideError" aria-label="Close"></button>
      </div>
    </div>
  </div>
  <ConfirmModal @confirm="deleteZone()" :title="$t('cta.$delete.title')" :message="$t('cta.$delete.message')" />
</template>

<script lang="ts">
import { defineComponent } from "vue";
import ConfirmModal from "@/components/Widgets/ConfirmModal.vue";
import SpinnerLoading from "@/components/Widgets/Spinner.vue";
import { Role, RoleInfo, UserInfo } from "@/utils/models/user.model";
import { removeNullProperties } from "@/utils/methods/remove_null_properties";
import API from "@/services/api/api.service";
import DiscardButton from "@/components/Widgets/DiscardButton.vue";
import config from "@/utils/const/config";
import { Zone } from "@/utils/models/zone.model";
import ZoneConsents from "@/components/FK/Zones/ZoneConsents.vue";
import RoleConsents from "../Roles/RoleConsents.vue";
import ZoneJourneySchemas from "./ZoneJourneySchemas.vue";

const  _default_consent_object = {
      hcp_consent_id: []
    } as Zone['consents_ids'];
export default defineComponent({
  name: "ZoneForm",
  components: {
    ConfirmModal,
    SpinnerLoading,
    DiscardButton,
    ZoneConsents,
    RoleConsents,
    ZoneJourneySchemas
  },
  data: function () {

    let zone = (this.$route.meta.zone ?? {}) as Zone;

    zone.consents_ids ??= _default_consent_object
    return {
      zone: zone as Zone,
      submitted: false,
      loading: false,
      errors: undefined as any,
    }
  },
  computed: {
    update(): boolean {
      return this.$route.meta.zone !== undefined && this.$route.meta.zone !== null;
    },
    locales(): string[] {
      return config.available_locales;
    },
    strategies(): UserInfo['assignment_strategy'][] {
      return ['by_center', 'by_hcp', 'self_registration'];
    },
    journey_roles(): RoleInfo[] {
      return (this.zone.zone_roles ?? []).filter((r: RoleInfo) => ( r.type === Role.patient || r.type === Role.caregiver)  );
    },
    show_journey_schemas():boolean {
      return this.update && (config.feature.journey_schemas ?? false);
    },
  },
  methods: {
    submit(event): void {
      this.errors = undefined;
      this.hideError();
      event.preventDefault();
      console.log("CENTER", this.zone);
      removeNullProperties<Zone>(this.zone as Zone);

      this.loading = true;
      if (this.update) {
        console.log('UPDATE', this.zone);
        API.updateZone((this.zone as Zone)).then((response) => {
          if (response.data.success === true) {
            this.$router.push(`/zone/${this.zone.id}/edit`);
          }
        }).catch((error) => {
          this.errors = error.response?.data?.errors;
          this.showError();
        }).finally(() => {
          this.loading = false;
        });
      } else {
        console.log('CREATE');

        API.createZone(this.zone as Zone).then((response) => {
          this.$router.push(`/zone/${response.data.data.id}/edit`);
        }).catch((error) => {
          this.errors = error.response?.data?.errors;
          this.showError();
        }).finally(() => {
          this.loading = false;
        });
      }
    },
    deleteZone(): void {
      this.loading = true;
      API.deleteZone((this.zone as Zone).id!).then((response) => {
        if (response.data.success === true) {
          this.$router.push('/zones');
        }
      }).catch((error) => {
        this.showError();
      }).finally(() => {
        console.log("DELETED_CENTER_ID", (this.zone as Zone).id);
        this.loading = false;
      })
    },
    showError() {
      const error = document.getElementById('errorMessage');
      error?.classList.add('show');
      setTimeout(() => {
        this.hideError();
      }, 5000)
    },
    hideError() {
      const error = document.getElementById('errorMessage');
      error?.classList.remove('show');
    },
    changeLang(lang?: string) {
      if (lang !== undefined) {
        this.zone.language_code = lang;
      }
    },
    changeStrategy(strategy?: string) {
      this.zone.consents_ids = _default_consent_object;
    },
  },
});
</script>